/**
 * metrics-ltv-adapter.ts — 数据中台 LTV 队列「真 API DTO → 页面消费形状」塑形（ADM-MTR-S005）
 *
 * GET /api/v1/metrics/ltv-cohorts 返回 snake_case DTO（服务端 DTO = packages/server
 * ltv-metrics-read-service.ts 的 listLtvCohorts 返回值；readiness 契约 dev-docs #392：顶层可选
 * revenue_ready + 行 revenue_usd_minor 可为 PENDING_INSTRUMENTATION 占位）。本 adapter 做：
 * - readiness 安全缺省：revenue_ready 缺省/false、或任一行 revenue 为 PENDING 占位 → revenueReady=false，
 *   此时全部热力值落 null（页面灰显）——**禁把回填前的 "0" 当真值渲染热力表**；
 * - cohort_size / payers_d7 / payers_d30 恒 live（user_attributions / 交易口径，与收入就绪度无关）；
 * - 金额保持 USD minor units number（页面 fmtUsdMinor 格式化）；computed_at 取行最大值作数据新鲜度。
 * retained_d1/d7/d30 不进视图模型：行为 sink 未点亮恒 NULL，且部分就绪的分母口径由 #275 决定
 * （见 ltv-metrics-read-service 文件头 invariant），呈现口径拍板前本页不消费留存列。
 * 契约缺字段落 null / pending，不编造数据。
 */

import { isPendingInstrumentation } from '@novel-admin/shared';

// ---------- 契约闭集（镜像服务端 LTV_COHORT_GROUP_BYS / 里程碑列） ----------

export const LTV_COHORT_GROUP_BYS = ['none', 'media_source', 'campaign'] as const;
export type LtvCohortGroupBy = (typeof LTV_COHORT_GROUP_BYS)[number];

/** D0–D90 里程碑闭集（key = DTO revenue_usd_minor 字段；days 供「未到期」判定） */
export const LTV_MILESTONES = [
  { key: 'd0', label: 'D0', days: 0 },
  { key: 'd1', label: 'D1', days: 1 },
  { key: 'd3', label: 'D3', days: 3 },
  { key: 'd7', label: 'D7', days: 7 },
  { key: 'd14', label: 'D14', days: 14 },
  { key: 'd30', label: 'D30', days: 30 },
  { key: 'd60', label: 'D60', days: 60 },
  { key: 'd90', label: 'D90', days: 90 },
] as const;
export type LtvMilestoneKey = (typeof LTV_MILESTONES)[number]['key'];

// ---------- DTO（snake_case，镜像服务端 listLtvCohorts 返回值 + #392 readiness 字段） ----------

type Pending = { status: 'PENDING_INSTRUMENTATION' };

type LtvRevenueMilestonesDto = Record<LtvMilestoneKey, string>;

export interface MetricsLtvCohortsResponse {
  currency: 'USD';
  financial_coverage?: {
    usd_ready: boolean;
    data_as_of: string | null;
    income_window: { start: string; end: string } | null;
  };
  /** 收入队列就绪闸（dev-docs #392）；本 worktree 服务端基线可能尚未输出 → 缺省视为未就绪 */
  revenue_ready?: boolean;
  rows: Array<{
    cohort_date: string;
    /** group_by=media_source → media_source；campaign → campaign_id；none → null */
    dim_key: string | null;
    /** group_by=campaign 时的 media_source 消歧列（campaign_id per-media-source） */
    dim_media_source: string | null;
    cohort_size: number;
    revenue_cohort_size?: number | null;
    /** 里程碑累计净收入（minor string）；未就绪可整体为 PENDING 占位 */
    revenue_usd_minor: LtvRevenueMilestonesDto | Pending;
    payers_d7: number;
    payers_d30: number;
    retained_d1: number | null;
    retained_d7: number | null;
    retained_d30: number | null;
    computed_at: string | null;
  }>;
}

// ---------- 视图模型（camelCase，页面消费） ----------

export interface MetricsLtvCohortRowView {
  /** cohort 日（cohort_metrics_daily 按日聚合；原型「安装周」为示意，真实粒度是日） */
  cohortDate: string;
  dimKey: string | null;
  dimMediaSource: string | null;
  /** live：cohort 规模（user_attributions 归因口径） */
  cohortSize: number;
  revenueCohortSize: number | null;
  /** revenueReady=false → null（热力格灰显，禁显 "0" 假值）；就绪 → 里程碑累计净收入 minor */
  revenueMinor: Record<LtvMilestoneKey, number> | null;
  /** live：D7 / D30 累计付费人数（交易口径） */
  payersD7: number;
  payersD30: number;
}

export interface MetricsLtvCohortsViewData {
  /** 收入队列是否就绪（false → 热力值 / 累积曲线 / 预估 LTV 全 PENDING 灰显） */
  revenueReady: boolean;
  incomeDataAsOf?: string | null;
  incomeWindow?: { start: string; end: string } | null;
  /** 行 computed_at 最大值（数据新鲜度标注；无行 → null） */
  computedAt: string | null;
  rows: MetricsLtvCohortRowView[];
}

/** minor string → number（非法/空 → 0；仅在 revenueReady 时调用，"0" 此时是真值） */
function minorNum(minor: string | null | undefined): number {
  if (!minor) return 0;
  const n = Number(minor);
  return Number.isFinite(n) ? n : 0;
}

export function adaptMetricsLtvCohorts(dto: MetricsLtvCohortsResponse): MetricsLtvCohortsViewData {
  // 安全缺省：顶层 revenue_ready 显式 true 且无任何行是 PENDING 占位才算就绪
  const revenueReady = dto.revenue_ready === true && dto.financial_coverage?.usd_ready === true;

  let computedAt: string | null = null;
  const rows = dto.rows.map((r) => {
    if (r.computed_at && (computedAt == null || r.computed_at > computedAt)) {
      computedAt = r.computed_at;
    }
    const rev =
      revenueReady &&
      r.revenue_cohort_size != null &&
      r.revenue_cohort_size >= 0 &&
      !isPendingInstrumentation(r.revenue_usd_minor) &&
      LTV_MILESTONES.every(
        (m) =>
          typeof (r.revenue_usd_minor as LtvRevenueMilestonesDto)[m.key] === 'string' &&
          /^-?\d+$/.test((r.revenue_usd_minor as LtvRevenueMilestonesDto)[m.key]) &&
          Number.isFinite(Number((r.revenue_usd_minor as LtvRevenueMilestonesDto)[m.key])),
      )
        ? (Object.fromEntries(
            LTV_MILESTONES.map((m) => [
              m.key,
              minorNum((r.revenue_usd_minor as LtvRevenueMilestonesDto)[m.key]),
            ]),
          ) as Record<LtvMilestoneKey, number>)
        : null;
    return {
      cohortDate: r.cohort_date,
      dimKey: r.dim_key,
      dimMediaSource: r.dim_media_source,
      cohortSize: r.cohort_size,
      revenueCohortSize: rev ? (r.revenue_cohort_size ?? null) : null,
      revenueMinor: rev,
      payersD7: r.payers_d7,
      payersD30: r.payers_d30,
    };
  });

  return {
    revenueReady,
    computedAt,
    rows,
    incomeDataAsOf: dto.financial_coverage?.data_as_of ?? null,
    incomeWindow: dto.financial_coverage?.income_window ?? null,
  };
}
